/**
 * ContractTemplatePreview - Pré-visualização fiel ao PDF gerado com multipáginas
 *
 * Renderiza o conteúdo do contrato em formato A4 a 96 DPI (794x1123px),
 * idêntico ao Chromium headless do html2pdf.app. Usa transform:scale para
 * caber no container disponível.
 *
 * Usa CSS compartilhado (contractStyles.ts) para paridade com Editor e PDF.
 *
 * @release v5.0.0 - 2026-05-01 - Responsivo (scale dinâmico via ResizeObserver)
 *                                + modo "embed" para uso dentro de ContractFullPreview.
 * @release v4.0.0 - 2026-02-16 - Migrado para 96 DPI com escala visual
 * @release v3.1.0 - 2026-02-16 - Migrado para contractStyles.ts compartilhado
 * @release v3.0.0 - 2026-02-16 - Suporte a multipáginas com medição de conteúdo
 */

import { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import {
  A4_WIDTH_PX,
  A4_HEIGHT_PX,
  CONTENT_WIDTH_PX,
  CONTRACT_CONTENT_CSS,
  PREVIEW_SCALE_FACTOR,
  MARGIN_LATERAL_PX,
  mmToPx,
  getUsableHeight,
  getPagePadding,
  getPreviewScaleFactor,
} from './contractStyles';

// Props do componente
interface ContractTemplatePreviewProps {
  /** HTML do template com variáveis {{chave}} */
  contentHtml: string;
  /** URL da imagem de fundo (signed URL do Storage) */
  backgroundPreviewUrl?: string | null;
  /** Se exibe número de página */
  showPageNumbers?: boolean;
  /** Posição da paginação: 'top-right' ou 'bottom-right' */
  pageNumberPosition?: string;
  /** Margem superior em mm quando há plano de fundo */
  bgMarginTopMm?: number;
  /** Margem inferior em mm quando há plano de fundo */
  bgMarginBottomMm?: number;
  /** Offset vertical em mm da borda para a paginação (padrão: 12mm) */
  paginationYOffsetMm?: number;
  /**
   * Modo "embed": renderiza apenas as páginas A4 (sem ScrollArea/Badge).
   * Usado quando este componente está aninhado dentro de outro orquestrador
   * (ex.: ContractFullPreview que junta capa + html + PDF base).
   * Quando true, recebe `embedScale` para sincronizar a escala com os
   * blocos vizinhos (capa/PDF base).
   */
  embed?: boolean;
  /** Fator de escala forçado (apenas em modo embed) */
  embedScale?: number;
}

/**
 * Dados fictícios para pré-visualização.
 * Substitui variáveis {{chave}} por valores de exemplo.
 */
export const SAMPLE_DATA: Record<string, string> = {
  nome_cliente: 'João da Silva',
  email_cliente: 'joao.silva@email.com',
  telefone_cliente: '(11) 99999-8888',
  cpf_cnpj: '123.456.789-00',
  endereco_cliente: 'Rua das Flores, 123 - São Paulo/SP',
  titulo_negocio: 'Contrato de Prestação de Serviço',
  valor_negocio: 'R$ 15.000,00',
  moeda: 'BRL',
  data_hoje: new Date().toLocaleDateString('pt-BR'),
  data_previsao: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString('pt-BR'),
  nome_vendedor: 'Maria Santos',
  nome_empresa: 'Minha Empresa LTDA',
  produtos_servicos: 'Consultoria Empresarial, Treinamento de Equipe',
};

export function ContractTemplatePreview({
  contentHtml,
  backgroundPreviewUrl,
  showPageNumbers = false,
  pageNumberPosition = 'bottom-right',
  bgMarginTopMm,
  bgMarginBottomMm,
  paginationYOffsetMm = 12,
  embed = false,
  embedScale,
}: ContractTemplatePreviewProps) {
  // Ref para o container externo (scroll/embed) e para medição de conteúdo
  const outerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);

  // Altura total do conteúdo medido
  const [contentHeight, setContentHeight] = useState(0);

  // Escala dinâmica baseada na largura do container externo
  const [scale, setScale] = useState<number>(embedScale ?? PREVIEW_SCALE_FACTOR);

  // Substitui variáveis pelos dados de exemplo
  const previewHtml = useMemo(() => {
    if (!contentHtml) return '';

    let html = contentHtml;

    // Substitui cada variável pelo valor de exemplo com destaque visual
    Object.entries(SAMPLE_DATA).forEach(([key, value]) => {
      const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
      html = html.replace(
        regex,
        `<span style="background:rgba(var(--primary-rgb,59,130,246),0.12);color:inherit;padding:0 3px;border-radius:3px;font-weight:500;" title="Variável: {{${key}}}">${value}</span>`,
      );
    });

    // Variáveis não mapeadas ficam destacadas em vermelho
    html = html.replace(
      /\{\{(\w+)\}\}/g,
      '<span style="background:rgba(239,68,68,0.12);color:#dc2626;padding:0 3px;border-radius:3px;font-weight:500;" title="Variável não mapeada">{{$1}}</span>',
    );

    return DOMPurify.sanitize(html);
  }, [contentHtml]);

  // Dimensões do PDF (independem da escala visual)
  const hasBackground = !!backgroundPreviewUrl;
  const usableHeight = getUsableHeight(hasBackground, showPageNumbers, pageNumberPosition, bgMarginTopMm, bgMarginBottomMm);
  const pagePadding = getPagePadding(hasBackground, showPageNumbers, pageNumberPosition, bgMarginTopMm, bgMarginBottomMm);

  /**
   * Mede a altura real do conteúdo renderizado no container oculto.
   */
  const measureContent = useCallback(() => {
    if (measureRef.current) {
      setContentHeight(measureRef.current.scrollHeight);
    }
  }, []);

  // Mede o conteúdo sempre que o HTML muda
  useEffect(() => {
    const timeoutId = setTimeout(measureContent, 50);
    return () => clearTimeout(timeoutId);
  }, [previewHtml, measureContent]);

  /**
   * Observa a largura do container externo e recalcula a escala.
   * Em modo embed, usa o `embedScale` recebido (mantém sincronia com pais).
   */
  useEffect(() => {
    if (embed && typeof embedScale === 'number') {
      setScale(embedScale);
      return;
    }
    if (!outerRef.current) return;

    const update = () => {
      const w = outerRef.current?.clientWidth ?? 0;
      setScale(getPreviewScaleFactor(w));
    };
    update();

    const ro = new ResizeObserver(update);
    ro.observe(outerRef.current);
    return () => ro.disconnect();
  }, [embed, embedScale]);

  // Número de páginas baseado na altura do conteúdo
  const totalPages = contentHeight > 0
    ? Math.max(1, Math.ceil(contentHeight / usableHeight))
    : 1;

  // Altura visual escalada de cada página
  const scaledPageHeight = A4_HEIGHT_PX * scale;
  const scaledPageWidth = A4_WIDTH_PX * scale;

  // Estado vazio - sem conteúdo
  if (!contentHtml) {
    return (
      <div className="border rounded-lg p-8 text-center text-muted-foreground min-h-[400px] flex items-center justify-center">
        <p>Escreva o conteúdo no editor para ver a pré-visualização aqui.</p>
      </div>
    );
  }

  /**
   * Conteúdo central: container oculto de medição + N páginas A4.
   * Renderizado tanto no modo standalone quanto no modo embed.
   */
  const pages = (
    <>
      {/* Injeção única do CSS compartilhado */}
      <style dangerouslySetInnerHTML={{ __html: CONTRACT_CONTENT_CSS }} />

      {/* Container oculto para medição da altura real */}
      <div
        ref={measureRef}
        aria-hidden="true"
        style={{
          position: 'absolute',
          visibility: 'hidden',
          width: `${CONTENT_WIDTH_PX}px`,
          fontFamily: 'Arial, sans-serif',
          fontSize: '12pt',
          lineHeight: '1.6',
          color: '#333333',
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
        }}
        className="contract-content"
        dangerouslySetInnerHTML={{ __html: previewHtml }}
      />

      {/* N páginas A4 a 96 DPI escaladas via transform */}
      {Array.from({ length: totalPages }).map((_, pageIndex) => {
        const offset = pageIndex * usableHeight;

        return (
          <div
            key={pageIndex}
            style={{
              width: `${scaledPageWidth}px`,
              height: `${scaledPageHeight}px`,
              flexShrink: 0,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${A4_WIDTH_PX}px`,
                height: `${A4_HEIGHT_PX}px`,
                transform: `scale(${scale})`,
                transformOrigin: 'top left',
                backgroundColor: '#ffffff',
                backgroundImage: backgroundPreviewUrl ? `url(${backgroundPreviewUrl})` : 'none',
                backgroundSize: '100% 100%',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                backgroundOrigin: 'border-box',
                backgroundClip: 'border-box',
                padding: pagePadding,
                fontFamily: 'Arial, sans-serif',
                fontSize: '12pt',
                lineHeight: '1.6',
                color: '#333333',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: '100%',
                  height: `${usableHeight}px`,
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                <div
                  className="contract-content"
                  dangerouslySetInnerHTML={{ __html: previewHtml }}
                  style={{
                    marginTop: `-${offset}px`,
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                  }}
                />
              </div>

              {showPageNumbers && (
                <div
                  style={{
                    position: 'absolute',
                    right: `${MARGIN_LATERAL_PX}px`,
                    ...(pageNumberPosition === 'top-right'
                      ? { top: hasBackground ? `${mmToPx(paginationYOffsetMm)}px` : '20px' }
                      : { bottom: hasBackground ? `${mmToPx(paginationYOffsetMm)}px` : '20px' }),
                    fontSize: '10pt',
                    color: '#666666',
                    fontFamily: 'Arial, sans-serif',
                  }}
                >
                  Página {pageIndex + 1} de {totalPages}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </>
  );

  // -------------------------------------------------------------------------
  // Modo embed: retorna apenas as páginas (orquestrador externo cuida do shell).
  // -------------------------------------------------------------------------
  if (embed) {
    return (
      <div
        ref={outerRef}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: `${24 * scale}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {pages}
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Modo standalone: shell completo com cabeçalho + ScrollArea.
  // -------------------------------------------------------------------------
  return (
    <div ref={outerRef} className="border rounded-lg overflow-hidden w-full">
      {/* Barra de informação */}
      <div className="flex items-center gap-2 px-4 py-2 border-b bg-muted/30">
        <Badge variant="outline" className="text-xs">Pré-visualização PDF</Badge>
        <span className="text-xs text-muted-foreground">
          Réplica fiel do PDF — {totalPages} página{totalPages > 1 ? 's' : ''}
        </span>
      </div>

      {/* Container com scroll - fundo cinza simulando área de trabalho */}
      <ScrollArea className="h-[500px]" style={{ backgroundColor: '#e5e5e5' }}>
        <div
          style={{
            backgroundColor: '#e5e5e5',
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: `${24 * scale}px`,
            minHeight: '100%',
            position: 'relative',
          }}
        >
          {pages}
        </div>
      </ScrollArea>
    </div>
  );
}
